import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';
import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import type { AuthStore } from '../state/useAuth';

const LOGIN_ART = '/assets/login/proposal-login-art.webp';
const PROPOSALS_LOGO = '/assets/login/proposals-logo.png';

/**
 * Full-screen signed-out experience. Authentication still goes through the
 * existing server endpoint, which exchanges the password with Cognito and
 * returns an HttpOnly session cookie to the browser.
 */
export function LoginScreen({ auth }: { auth: AuthStore }): ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const secret = password;
    setPassword('');
    void auth.signIn(username.trim(), secret);
  };

  const updateUsername = (value: string) => {
    if (auth.error) auth.clearError();
    setUsername(value);
  };

  const updatePassword = (value: string) => {
    if (auth.error) auth.clearError();
    setPassword(value);
  };

  return (
    <main className="login-screen">
      <section className="login-visual" aria-labelledby="login-hero-title">
        <img
          className="login-visual-image"
          src={LOGIN_ART}
          alt="Layered proposal pages move through a blue architectural studio toward final presentation."
        />
        <div className="login-visual-shade" aria-hidden="true" />
        <h1 id="login-hero-title" className="login-hero-title">
          The thoughtful way to build proposals.
        </h1>
      </section>

      <section className="login-panel" aria-label="Sign in">
        <div className="login-panel-inner">
          <img className="login-logo" src={PROPOSALS_LOGO} alt="Proposals" />

          {auth.error ? (
            <p className="login-error" role="alert">
              {auth.error}
            </p>
          ) : null}

          <form className="login-form" onSubmit={submit}>
            <label className="login-field">
              <span className="login-label">Email</span>
              <input
                className="login-input"
                type="email"
                name="username"
                value={username}
                onChange={(event) => updateUsername(event.target.value)}
                placeholder="you@company.com"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                autoFocus
                required
              />
            </label>

            <label className="login-field">
              <span className="login-label">Password</span>
              <span className="login-password-wrap">
                <input
                  className="login-input login-password-input"
                  type={passwordVisible ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(event) => updatePassword(event.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  className="login-password-toggle"
                  type="button"
                  onClick={() => setPasswordVisible((visible) => !visible)}
                  aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                  aria-pressed={passwordVisible}
                >
                  {passwordVisible ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </span>
            </label>

            <button className="login-submit" type="submit" disabled={auth.busy}>
              <span>{auth.busy ? 'Signing in' : 'Sign in'}</span>
              <ArrowRight className="login-submit-arrow" size={22} weight="bold" />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
